/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import CaptureHeader from '../CaptureHeader';
import ReferenceTooltip from './ReferenceTooltip';
import { DEFAULT_CUSTOMER_REFERENCE_LABEL, resolveCustomerReferenceLabel } from '../../utils/customerReferenceLabel';

import './capture.css';

const Done = ({
  assignment,
  items,
  tenant,
  branding,
  workflow,
  completedItemsMedia = {},
  token,
  notes = [],
  content = null,
}) => {
  const currentTheme = branding.theme || 'light';
  const [showTooltip, setShowTooltip] = useState(false);
  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const tooltipTriggerRef = useRef(null);
  const nameTooltipTriggerRef = useRef(null);
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(workflow);

  // Count completed media by type (identifier counts as image)
  const allCompletedMedia = Object.values(completedItemsMedia || {})
    .flatMap((mediaArr) => Array.isArray(mediaArr) ? mediaArr : [mediaArr])
    .filter(Boolean);

  const imageCount = allCompletedMedia.filter((media) => media.type === 'photo' || media.type === 'identifier').length;
  const videoCount = allCompletedMedia.filter((media) => media.type === 'video').length;
  const fileCount = allCompletedMedia.filter((media) => media.type === 'file').length;
  const noteCount = notes.length;

  const thankyouTitle = content?.title ?? workflow?.branding?.thankyouTitle;
  const thankyouMessage = content?.message ?? workflow?.branding?.thankyouMessage;
  const showWhatHappensNext = content?.showWhatHappensNext ?? true;
  const whatHappensNextDescription = workflow.branding?.whatHappensNextDescription;

  return (
    <div data-theme={currentTheme} className="min-h-screen flex flex-col bg-base-100 text-base-content overflow-x-hidden capture-container landscape-root">
      {/* ── LEFT SIDEBAR ── */}
      <div className="landscape-sidebar w-full max-w-full mx-auto">
        {/* Notch Spacer */}
        <div className="h-[env(safe-area-inset-top,20px)] bg-base-100" />

        {/* Header */}
        <div className="w-full">
          <CaptureHeader expiresAt={assignment?.expiresAt} brandLogo={workflow.branding?.logoUrl} branding={branding} type='done' token={token} />
        </div>

        <div className="px-3 py-4 flex flex-col">
          {/* Name and Reference */}
          <div className="w-full mb-3 flex flex-col gap-1">
            <div className="relative min-w-0">
              <h2
                ref={nameTooltipTriggerRef}
                className="text-base-content text-[20px] font-bold tracking-tighter leading-none truncate cursor-pointer transition-opacity active:opacity-70 hover:opacity-80"
                onClick={() => setShowNameTooltip(!showNameTooltip)}
              >
                {assignment.submitterName || assignment.workflowName || 'Mobile Capture'}
              </h2>

              <ReferenceTooltip
                assignment={assignment}
                workflow={workflow}
                show={showNameTooltip}
                onClose={() => setShowNameTooltip(false)}
                theme={currentTheme}
                triggerRef={nameTooltipTriggerRef}
                type="name"
              />
            </div>
            <div className="text-[14px] text-base-content leading-tight shrink-0">
              <div className="flex flex-wrap justify-between items-start overflow-visible custom-line-break w-full">
                <span>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:{" "}</span>
                <span className="inline-block text-right shrink max-w-full break-all">
                  <button
                    type="button"
                    ref={tooltipTriggerRef}
                    onClick={() => setShowTooltip(!showTooltip)}
                    className="text-primary underline decoration-dashed focus:outline-none cursor-pointer relative z-[30] custom-line-break"
                  >
                    {assignment.customerReferenceNumber}
                  </button>
                </span>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showTooltip}
                  onClose={() => setShowTooltip(false)}
                  theme={currentTheme}
                  triggerRef={tooltipTriggerRef}
                  className="tooltip-for-landscap-review"
                />
              </div>
            </div>
          </div>

          {/* WHAT HAPPENS NEXT (Hidden in Portrait, shown in Landscape left panel) */}
          {showWhatHappensNext && (
            <div className="w-full text-left flex-1 hide-in-portrait mt-8">
              <h3 className="text-[18px] font-bold text-base-content tracking-tight mb-3">What happens next</h3>
              <div className="w-full bg-primary/30 rounded-[8px] p-5 border border-primary/5">
                <p className="text-[14px] leading-tight text-base-content custom-line-break">
                  {whatHappensNextDescription || 'Your submitted imagery will be reviewed. You will receive updates once the process is complete.'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT MAIN PANEL (landscape: item counts + safe-to-close) ── */}
      <div className="landscape-main landscape-main-done landscape-portrait-counts flex flex-col items-center justify-start gap-8 px-4 py-2 w-full max-w-full mx-auto">
        {/* Thank You Message */}
        <div className="w-full max-w-full text-left">
          <h1 className="text-[24px] font-medium text-base-content mb-2 tracking-tight custom-line-break">
            {(thankyouTitle || 'Media submitted!').replaceAll('{name}', assignment.submitterName || '')}
          </h1>
          <p className="text-base-content text-[16px] custom-line-break">
            {thankyouMessage || 'Your items have been securely sent.'}
          </p>
        </div>

        {/* Media Count Cards - Only Images and Videos */}
        <div className="w-full max-w-full grid grid-cols-2 gap-6 px-10 justify-center">
          {imageCount > 0 && (
            <div className="flex-1 bg-[#D9D9D9] rounded-[12px] p-3 flex flex-col items-center justify-center">
              <span className="text-[16px] font-normal text-black">Images</span>
              <span className="text-[40px] font-normal text-black mt-2 leading-none">{imageCount}</span>
            </div>
          )}
          {fileCount > 0 && (
            <div className="flex-1 bg-[#D9D9D9] rounded-[8px] p-3 flex flex-col items-center justify-center">
              <span className="text-[16px] font-normal text-black">Files</span>
              <span className="text-[40px] font-normal text-black mt-[5px] leading-none">{fileCount}</span>
            </div>
          )}
          {videoCount > 0 && (
            <div className="flex-1 bg-[#D9D9D9] rounded-[12px] p-3 flex flex-col items-center justify-center">
              <span className="text-[16px] font-normal text-black">Videos</span>
              <span className="text-[40px] font-normal text-black mt-2 leading-none">{videoCount}</span>
            </div>
          )}
          {noteCount > 0 && (
            <div className="flex-1 bg-[#D9D9D9] rounded-[12px] p-3 flex flex-col items-center justify-center">
              <span className="text-[16px] font-normal text-black">Notes</span>
              <span className="text-[40px] font-normal text-black mt-2 leading-none">{noteCount}</span>
            </div>
          )}
        </div>

        {/* WHAT HAPPENS NEXT (Hidden in Landscape, shown in Portrait right panel) */}
        {showWhatHappensNext && (
          <div className="w-full max-w-full text-left hide-in-landscape">
            <h3 className="text-[18px] font-bold text-base-content tracking-tight mb-3">What happens next</h3>
            <div className="w-full bg-primary/30 rounded-[8px] p-5 border border-primary/5">
              <p className="text-[16px] leading-tight text-base-content custom-line-break">
                {whatHappensNextDescription || 'Your submitted imagery will be reviewed. You will receive updates once the process is complete.'}
              </p>
            </div>
          </div>
        )}

        {/* Safe to close text */}
        <div className="w-full text-center mt-4">
          <p className="text-[22px] font-normal text-base-content">Safe to close this window</p>
        </div>
      </div>

      {/* Bottom Spacer */}
      <div className="h-[env(safe-area-inset-bottom,40px)] bg-base-100" />
    </div>
  );
};

export default Done;

Done.propTypes = {
  assignment: PropTypes.shape({
    expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitterName: PropTypes.string,
    workflowName: PropTypes.string,
    customerReferenceNumber: PropTypes.string,
  }),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  tenant: PropTypes.any,
  branding: PropTypes.shape({
    theme: PropTypes.string,
  }),
  completedItemsMedia: PropTypes.object,
  notes: PropTypes.array,
  workflow: PropTypes.shape({
    branding: PropTypes.shape({
      thankyouTitle: PropTypes.string,
      thankyouMessage: PropTypes.string,
      whatHappensNextDescription: PropTypes.string,
      logoUrl: PropTypes.string,
    }),
  }),
  token: PropTypes.string,
  content: PropTypes.shape({
    title: PropTypes.string,
    message: PropTypes.string,
    showWhatHappensNext: PropTypes.bool,
  }),
};
